// Iter 86.16 / Phase E7 Phase C — Unified Employee + User Onboarding
//
// Route: /admin/onboard
//
// Three-tab UX:
//   1. "Employee + User"   (combined create — happy path)
//   2. "Link existing"     (User ↔ Employee retroactive linkage)
//   3. "Standalone user"   (exception path — external auditors, vendor portals)
//
// Department / Designation / Reporting Manager are deliberately ONLY on
// the Employee tab. The User tab on the right is identity-only (email /
// name / role / phone / password) — org-chart fields derive from the
// linked Employee record per the PRD's "single source of truth"
// invariant.
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
    UsersThree, ArrowLeft, FloppyDisk, LinkSimple, Warning, UserCircle, FileCsv,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import PageHeader from "../../components/layout/PageHeader";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { ROLE_LABELS } from "../../contexts/AuthContext";
import BulkCSVTab from "./BulkCSVTab";


const TABS = [
    { id: "combined", label: "Employee + User", icon: UsersThree, description: "Create an employee record AND a system login in one atomic step." },
    { id: "link", label: "Link existing", icon: LinkSimple, description: "Wire an existing user account to an existing employee record." },
    { id: "standalone", label: "Standalone user", icon: UserCircle, description: "Exception path — create a user with no employee record (auditor, vendor portal, integration account)." },
    { id: "bulk_csv", label: "Bulk CSV", icon: FileCsv, description: "Onboard up to 5,000 employees + users via a validated CSV upload. Two-stage: validate → preview → commit." },
];


function FieldGrid({ children }) {
    return <div className="grid grid-cols-1 md:grid-cols-2 gap-3">{children}</div>;
}


// ─────────────────────────────────────────────────────────────────────
// Tab 1 — Combined create
// ─────────────────────────────────────────────────────────────────────
function CombinedTab({ roles, departments, designations, employees }) {
    const navigate = useNavigate();
    const blank = {
        user: { email: "", name: "", role: "viewer", password: "", phone: "" },
        employee: {
            full_name: "",
            official_email: "",
            personal_email: "",
            mobile: "",
            employment_category: "Employee",
            department: "",
            designation: "",
            date_of_joining: "",
            reporting_manager_employee_id: "",
            status: "Active",
            notes: "",
        },
    };
    const [form, setForm] = useState(blank);
    const [submitting, setSubmitting] = useState(false);

    const updateUser = (patch) => setForm((p) => ({ ...p, user: { ...p.user, ...patch } }));
    const updateEmp = (patch) => setForm((p) => ({ ...p, employee: { ...p.employee, ...patch } }));

    const fillFromUser = () => {
        // One-click prefill of "official email = user email" and "employee full name = user name"
        updateEmp({
            full_name: form.user.name || form.employee.full_name,
            official_email: form.user.email || form.employee.official_email,
        });
        toast.success("Employee identity prefilled from User identity");
    };

    const submit = async () => {
        if (!form.user.email || !form.user.name || !form.user.password || !form.user.phone) {
            toast.error("User: email, name, password, phone are all required");
            return;
        }
        if (form.user.password.length < 8) {
            toast.error("Password must be at least 8 characters");
            return;
        }
        if (!form.employee.full_name) {
            toast.error("Employee: full name is required");
            return;
        }
        setSubmitting(true);
        try {
            const payload = {
                user: { ...form.user, email: form.user.email.toLowerCase().trim() },
                employee: {
                    ...form.employee,
                    date_of_joining: form.employee.date_of_joining || null,
                    reporting_manager_employee_id: form.employee.reporting_manager_employee_id || null,
                },
            };
            const r = await api.post("/admin/onboard/employee-and-user", payload);
            toast.success(`Onboarded ${r.data.employee.employee_code} (${r.data.user.email})`);
            navigate(`/users`);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="space-y-6" data-testid="combined-onboard-form">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* User block */}
                <div className="bg-white border border-slate-200 rounded-lg p-5 space-y-4">
                    <div className="flex items-center justify-between">
                        <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                            <UserCircle className="w-5 h-5 text-indigo-500" />
                            System login
                        </h3>
                        <span className="text-xs text-slate-500">→ identity only</span>
                    </div>
                    <FieldGrid>
                        <div>
                            <Label className="text-xs">Full name <span className="text-rose-500">*</span></Label>
                            <Input value={form.user.name} onChange={(e) => updateUser({ name: e.target.value })} placeholder="John Doe" data-testid="combined-user-name" />
                        </div>
                        <div>
                            <Label className="text-xs">Role <span className="text-rose-500">*</span></Label>
                            <select
                                value={form.user.role}
                                onChange={(e) => updateUser({ role: e.target.value })}
                                className="mt-1 w-full h-10 px-3 text-sm border border-slate-200 rounded-md bg-white"
                                data-testid="combined-user-role"
                            >
                                {roles.map((r) => (
                                    <option key={r.code} value={r.code}>{r.name} ({r.code})</option>
                                ))}
                            </select>
                        </div>
                    </FieldGrid>
                    <div>
                        <Label className="text-xs">Login email <span className="text-rose-500">*</span></Label>
                        <Input
                            type="email"
                            value={form.user.email}
                            onChange={(e) => updateUser({ email: e.target.value.toLowerCase() })}
                            placeholder="john@reedos.in"
                            data-testid="combined-user-email"
                        />
                    </div>
                    <FieldGrid>
                        <div>
                            <Label className="text-xs">Contact phone <span className="text-rose-500">*</span></Label>
                            <Input value={form.user.phone} onChange={(e) => updateUser({ phone: e.target.value })} placeholder="+91 98xxx xxxxx" data-testid="combined-user-phone" />
                            <p className="text-[11px] text-slate-500 mt-0.5">Shown on every proposal as &ldquo;Submitted By&rdquo;.</p>
                        </div>
                        <div>
                            <Label className="text-xs">Temporary password <span className="text-rose-500">*</span></Label>
                            <Input
                                type="text"
                                minLength={8}
                                value={form.user.password}
                                onChange={(e) => updateUser({ password: e.target.value })}
                                placeholder="Minimum 8 chars"
                                className="font-mono"
                                data-testid="combined-user-password"
                            />
                            <p className="text-[11px] text-slate-500 mt-0.5">Share securely.</p>
                        </div>
                    </FieldGrid>
                </div>

                {/* Employee block */}
                <div className="bg-white border border-slate-200 rounded-lg p-5 space-y-4">
                    <div className="flex items-center justify-between">
                        <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                            <UsersThree className="w-5 h-5 text-emerald-500" />
                            Employee record
                        </h3>
                        <button
                            type="button"
                            className="text-xs text-indigo-600 hover:underline"
                            onClick={fillFromUser}
                            data-testid="combined-prefill-from-user-btn"
                        >
                            Prefill from User →
                        </button>
                    </div>
                    <FieldGrid>
                        <div>
                            <Label className="text-xs">Full name (HR) <span className="text-rose-500">*</span></Label>
                            <Input value={form.employee.full_name} onChange={(e) => updateEmp({ full_name: e.target.value })} placeholder="John Doe" data-testid="combined-emp-full-name" />
                        </div>
                        <div>
                            <Label className="text-xs">Employment category</Label>
                            <select
                                value={form.employee.employment_category}
                                onChange={(e) => updateEmp({ employment_category: e.target.value })}
                                className="mt-1 w-full h-10 px-3 text-sm border border-slate-200 rounded-md bg-white"
                                data-testid="combined-emp-category"
                            >
                                {["Employee", "Consultant", "Advisor", "Intern"].map((c) => (
                                    <option key={c} value={c}>{c}</option>
                                ))}
                            </select>
                        </div>
                    </FieldGrid>
                    <FieldGrid>
                        <div>
                            <Label className="text-xs">Department</Label>
                            <select
                                value={form.employee.department}
                                onChange={(e) => updateEmp({ department: e.target.value })}
                                className="mt-1 w-full h-10 px-3 text-sm border border-slate-200 rounded-md bg-white"
                                data-testid="combined-emp-department"
                            >
                                <option value="">— pick department —</option>
                                {departments.map((d) => (
                                    <option key={d.id} value={d.name}>{d.name}</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <Label className="text-xs">Designation</Label>
                            <select
                                value={form.employee.designation}
                                onChange={(e) => updateEmp({ designation: e.target.value })}
                                className="mt-1 w-full h-10 px-3 text-sm border border-slate-200 rounded-md bg-white"
                                data-testid="combined-emp-designation"
                            >
                                <option value="">— pick designation —</option>
                                {designations.map((d) => (
                                    <option key={d.id} value={d.name}>{d.name}</option>
                                ))}
                            </select>
                        </div>
                    </FieldGrid>
                    <FieldGrid>
                        <div>
                            <Label className="text-xs">Reporting manager</Label>
                            <select
                                value={form.employee.reporting_manager_employee_id}
                                onChange={(e) => updateEmp({ reporting_manager_employee_id: e.target.value })}
                                className="mt-1 w-full h-10 px-3 text-sm border border-slate-200 rounded-md bg-white"
                                data-testid="combined-emp-reporting-manager"
                            >
                                <option value="">— none —</option>
                                {employees.map((em) => (
                                    <option key={em.id} value={em.id}>{em.full_name} ({em.employee_code})</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <Label className="text-xs">Date of joining</Label>
                            <Input type="date" value={form.employee.date_of_joining} onChange={(e) => updateEmp({ date_of_joining: e.target.value })} data-testid="combined-emp-doj" />
                        </div>
                    </FieldGrid>
                    <div>
                        <Label className="text-xs">Notes</Label>
                        <Textarea rows={2} value={form.employee.notes} onChange={(e) => updateEmp({ notes: e.target.value })} placeholder="Optional onboarding context" data-testid="combined-emp-notes" />
                    </div>
                </div>
            </div>

            <div className="sticky bottom-0 bg-white border border-slate-200 rounded-lg px-5 py-3 flex items-center justify-between" data-testid="combined-actions-bar">
                <div className="text-xs text-slate-500">
                    Atomic: if either side fails, the other is rolled back.
                </div>
                <div className="flex items-center gap-2">
                    <Link to="/users"><Button variant="outline" data-testid="combined-cancel-btn">Cancel</Button></Link>
                    <Button onClick={submit} disabled={submitting} data-testid="combined-submit-btn">
                        <FloppyDisk className="w-4 h-4 mr-2" />
                        {submitting ? "Onboarding..." : "Create Employee + User"}
                    </Button>
                </div>
            </div>
        </div>
    );
}


// ─────────────────────────────────────────────────────────────────────
// Tab 2 — Link existing
// ─────────────────────────────────────────────────────────────────────
function LinkTab({ unlinkedEmployees, unlinkedUsers, onLinked }) {
    const [employeeId, setEmployeeId] = useState("");
    const [userId, setUserId] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const selectedEmp = unlinkedEmployees.find((e) => e.id === employeeId);
    const selectedUser = unlinkedUsers.find((u) => u.id === userId);

    const submit = async () => {
        if (!employeeId || !userId) {
            toast.error("Pick both an employee and a user");
            return;
        }
        setSubmitting(true);
        try {
            await api.post("/admin/onboard/link-user-to-employee", { employee_id: employeeId, user_id: userId });
            toast.success(`Linked ${selectedEmp?.employee_code} ↔ ${selectedUser?.email}`);
            setEmployeeId("");
            setUserId("");
            onLinked?.();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="space-y-6" data-testid="link-onboard-form">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-white border border-slate-200 rounded-lg p-5 space-y-3">
                    <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                        <UsersThree className="w-5 h-5 text-emerald-500" />
                        Employee ({unlinkedEmployees.length} unlinked)
                    </h3>
                    <p className="text-xs text-slate-500">Employees with no system login yet.</p>
                    <select
                        value={employeeId}
                        onChange={(e) => setEmployeeId(e.target.value)}
                        className="w-full h-10 px-3 text-sm border border-slate-200 rounded-md bg-white"
                        data-testid="link-employee-select"
                    >
                        <option value="">— pick an employee —</option>
                        {unlinkedEmployees.map((em) => (
                            <option key={em.id} value={em.id}>{em.full_name} · {em.employee_code} · {em.department || "—"}</option>
                        ))}
                    </select>
                    {selectedEmp && (
                        <div className="bg-slate-50 border border-slate-200 rounded-md p-3 text-xs text-slate-700 space-y-1" data-testid="link-employee-preview">
                            <div><strong>{selectedEmp.full_name}</strong> ({selectedEmp.employee_code})</div>
                            <div className="text-slate-500">Dept: {selectedEmp.department || "—"} · Designation: {selectedEmp.designation || "—"} · Status: {selectedEmp.status || "—"}</div>
                            {selectedEmp.official_email && <div className="text-slate-500">Email: {selectedEmp.official_email}</div>}
                        </div>
                    )}
                </div>

                <div className="bg-white border border-slate-200 rounded-lg p-5 space-y-3">
                    <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                        <UserCircle className="w-5 h-5 text-indigo-500" />
                        User ({unlinkedUsers.length} unlinked)
                    </h3>
                    <p className="text-xs text-slate-500">Active users with no employee record.</p>
                    <select
                        value={userId}
                        onChange={(e) => setUserId(e.target.value)}
                        className="w-full h-10 px-3 text-sm border border-slate-200 rounded-md bg-white"
                        data-testid="link-user-select"
                    >
                        <option value="">— pick a user —</option>
                        {unlinkedUsers.map((u) => (
                            <option key={u.id} value={u.id}>{u.name} · {u.email} · {ROLE_LABELS[u.role] || u.role}</option>
                        ))}
                    </select>
                    {selectedUser && (
                        <div className="bg-slate-50 border border-slate-200 rounded-md p-3 text-xs text-slate-700 space-y-1" data-testid="link-user-preview">
                            <div><strong>{selectedUser.name}</strong> · {selectedUser.email}</div>
                            <div className="text-slate-500">Role: {ROLE_LABELS[selectedUser.role] || selectedUser.role}</div>
                        </div>
                    )}
                </div>
            </div>

            <div className="sticky bottom-0 bg-white border border-slate-200 rounded-lg px-5 py-3 flex items-center justify-between" data-testid="link-actions-bar">
                <div className="text-xs text-slate-500">
                    1:1 invariant — neither side may already be linked to another counterpart.
                </div>
                <Button onClick={submit} disabled={submitting || !employeeId || !userId} data-testid="link-submit-btn">
                    <LinkSimple className="w-4 h-4 mr-2" />
                    {submitting ? "Linking..." : "Link Employee ↔ User"}
                </Button>
            </div>
        </div>
    );
}


// ─────────────────────────────────────────────────────────────────────
// Tab 3 — Standalone user
// ─────────────────────────────────────────────────────────────────────
function StandaloneTab({ roles }) {
    const navigate = useNavigate();
    const [form, setForm] = useState({ email: "", name: "", role: "viewer", password: "", phone: "" });
    const [reason, setReason] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const submit = async () => {
        if (!form.email || !form.name || !form.password || !form.phone) {
            toast.error("All user fields are required");
            return;
        }
        if (form.password.length < 8) {
            toast.error("Password must be at least 8 characters");
            return;
        }
        if (!reason.trim()) {
            toast.error("Provide a reason — the audit log requires it");
            return;
        }
        setSubmitting(true);
        try {
            await api.post("/admin/onboard/standalone-user", {
                user: { ...form, email: form.email.toLowerCase().trim() },
                reason: reason.trim(),
            });
            toast.success(`Standalone user '${form.email}' created`);
            navigate("/users");
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="space-y-6" data-testid="standalone-onboard-form">
            <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-sm text-amber-800 flex items-start gap-2" data-testid="standalone-warning">
                <Warning className="w-5 h-5 flex-shrink-0 mt-0.5" />
                <div>
                    <div className="font-medium">Exception path</div>
                    <div className="text-xs">Use for external auditors, vendor portal access, automated integration accounts — users who legitimately have NO employee record. The reason is logged in the audit trail.</div>
                </div>
            </div>

            <div className="bg-white border border-slate-200 rounded-lg p-5 space-y-4">
                <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                    <UserCircle className="w-5 h-5 text-indigo-500" />
                    Standalone user
                </h3>
                <FieldGrid>
                    <div>
                        <Label className="text-xs">Full name <span className="text-rose-500">*</span></Label>
                        <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Jane Auditor" data-testid="standalone-name" />
                    </div>
                    <div>
                        <Label className="text-xs">Role <span className="text-rose-500">*</span></Label>
                        <select
                            value={form.role}
                            onChange={(e) => setForm({ ...form, role: e.target.value })}
                            className="mt-1 w-full h-10 px-3 text-sm border border-slate-200 rounded-md bg-white"
                            data-testid="standalone-role"
                        >
                            {roles.map((r) => (
                                <option key={r.code} value={r.code}>{r.name} ({r.code})</option>
                            ))}
                        </select>
                    </div>
                </FieldGrid>
                <FieldGrid>
                    <div>
                        <Label className="text-xs">Login email <span className="text-rose-500">*</span></Label>
                        <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value.toLowerCase() })} placeholder="auditor@external.com" data-testid="standalone-email" />
                    </div>
                    <div>
                        <Label className="text-xs">Contact phone <span className="text-rose-500">*</span></Label>
                        <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+91 98xxx xxxxx" data-testid="standalone-phone" />
                    </div>
                </FieldGrid>
                <div>
                    <Label className="text-xs">Temporary password <span className="text-rose-500">*</span></Label>
                    <Input type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Minimum 8 chars" className="font-mono" data-testid="standalone-password" />
                </div>
                <div>
                    <Label className="text-xs">Reason (audit log) <span className="text-rose-500">*</span></Label>
                    <Textarea
                        rows={3}
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        placeholder="e.g. External GST auditor for Q2 FY26 filing review; access expires 2026-09-30."
                        data-testid="standalone-reason"
                    />
                </div>
            </div>

            <div className="sticky bottom-0 bg-white border border-slate-200 rounded-lg px-5 py-3 flex items-center justify-between" data-testid="standalone-actions-bar">
                <div className="text-xs text-slate-500">
                    No employee record will be created. Dept/Designation will show &ldquo;—&rdquo; on User detail.
                </div>
                <div className="flex items-center gap-2">
                    <Link to="/users"><Button variant="outline" data-testid="standalone-cancel-btn">Cancel</Button></Link>
                    <Button onClick={submit} disabled={submitting} data-testid="standalone-submit-btn">
                        <FloppyDisk className="w-4 h-4 mr-2" />
                        {submitting ? "Creating..." : "Create standalone user"}
                    </Button>
                </div>
            </div>
        </div>
    );
}


// ─────────────────────────────────────────────────────────────────────
// Main page
// ─────────────────────────────────────────────────────────────────────
export default function UnifiedOnboarding() {
    const [activeTab, setActiveTab] = useState("combined");
    const [loading, setLoading] = useState(true);
    const [roles, setRoles] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [designations, setDesignations] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [unlinkedEmployees, setUnlinkedEmployees] = useState([]);
    const [unlinkedUsers, setUnlinkedUsers] = useState([]);

    const loadAll = useCallback(async () => {
        setLoading(true);
        try {
            const [rolesResp, deptResp, desigResp, empResp, unlinkedEmpsResp, unlinkedUsersResp] = await Promise.all([
                api.get("/admin/role/list"),
                api.get("/hr/department/list").catch(() => ({ data: { items: [] } })),
                api.get("/hr/designation/list").catch(() => ({ data: { items: [] } })),
                api.get("/hr/employee/list?limit=500").catch(() => ({ data: { items: [] } })),
                api.get("/admin/onboard/unlinked-employees").catch(() => ({ data: { items: [] } })),
                api.get("/admin/onboard/unlinked-users").catch(() => ({ data: { items: [] } })),
            ]);
            setRoles((rolesResp.data.items || []).filter((r) => r.is_active && r.code !== "super_admin"));
            setDepartments(deptResp.data.items || []);
            setDesignations(desigResp.data.items || []);
            setEmployees(empResp.data.items || []);
            setUnlinkedEmployees(unlinkedEmpsResp.data.items || []);
            setUnlinkedUsers(unlinkedUsersResp.data.items || []);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { loadAll(); }, [loadAll]);

    const activeTabDef = useMemo(() => TABS.find((t) => t.id === activeTab) || TABS[0], [activeTab]);

    if (loading) {
        return <div className="p-6 text-sm text-slate-500" data-testid="onboard-loading">Loading...</div>;
    }

    return (
        <div className="space-y-6" data-testid="unified-onboarding-page">
            <PageHeader
                title="Onboard new Employee / User"
                subtitle="Single surface that creates employees + users together, links existing pairs, or provisions exceptional standalone accounts."
                breadcrumbs={[{ label: "Administration" }, { label: "Onboard" }]}
                actions={
                    <Link to="/users">
                        <Button variant="outline" data-testid="back-to-users-btn">
                            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Users
                        </Button>
                    </Link>
                }
            />

            {/* Tab strip */}
            <div className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid="onboard-tab-strip">
                <div className="flex border-b border-slate-200">
                    {TABS.map((t) => {
                        const Icon = t.icon;
                        const active = activeTab === t.id;
                        return (
                            <button
                                key={t.id}
                                type="button"
                                onClick={() => setActiveTab(t.id)}
                                className={`flex-1 px-5 py-4 text-left border-r border-slate-200 last:border-r-0 transition-colors ${
                                    active
                                        ? "bg-indigo-50 border-b-2 border-b-indigo-600"
                                        : "hover:bg-slate-50 border-b-2 border-b-transparent"
                                }`}
                                data-testid={`tab-${t.id}`}
                            >
                                <div className="flex items-center gap-2">
                                    <Icon className={`w-5 h-5 ${active ? "text-indigo-600" : "text-slate-500"}`} />
                                    <span className={`font-semibold text-sm ${active ? "text-indigo-700" : "text-slate-700"}`}>{t.label}</span>
                                </div>
                                <p className="text-xs text-slate-500 mt-1.5 leading-snug">{t.description}</p>
                            </button>
                        );
                    })}
                </div>
            </div>

            {activeTab === "combined" && (
                <CombinedTab roles={roles} departments={departments} designations={designations} employees={employees} />
            )}
            {activeTab === "link" && (
                <LinkTab unlinkedEmployees={unlinkedEmployees} unlinkedUsers={unlinkedUsers} onLinked={loadAll} />
            )}
            {activeTab === "standalone" && (
                <StandaloneTab roles={roles} />
            )}
            {activeTab === "bulk_csv" && (
                <BulkCSVTab />
            )}
        </div>
    );
}
